import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCan, useCanOrganizationWide } from "@/lib/policy";
import {
  boardFormToInput,
  boardToForm,
  emptyBoard,
  JobBoardForm,
  type BoardFormValues,
} from "./-components/job-board-form";
import { JobBoardTable } from "./-components/job-board-table";
import type { BoardRow } from "@/routes/_authenticated/_workforce/hiring/-components/job-board-table-shared";

export const Route = createFileRoute("/_authenticated/_workforce/hiring/job-boards")({
  component: JobBoardsPage,
});

/** Boards group postings under a shared public page; they are optional. */
function JobBoardsPage() {
  const { facilityId } = useFacility();
  const canManage = useCan("hiring:manage");
  const managesEverywhere = useCanOrganizationWide("hiring:manage");
  const queryClient = useQueryClient();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<BoardRow | null>(null);
  const [deactivating, setDeactivating] = useState<BoardRow | null>(null);

  const boards = useQuery(orpc.workforce.hiring.listBoards.queryOptions({ input: { facilityId } }));
  const jobs = useQuery(orpc.workforce.hiring.listJobs.queryOptions({ input: { facilityId } }));
  const facilities = useQuery(
    orpc.administration.facilities.list.queryOptions({ input: { includeInactive: false } }),
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: orpc.workforce.hiring.key() });

  const create = useMutation(
    orpc.workforce.hiring.createBoard.mutationOptions({
      onSuccess: async () => {
        toast.success("Board created");
        setCreating(false);
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const update = useMutation(
    orpc.workforce.hiring.updateBoard.mutationOptions({
      onSuccess: async () => {
        toast.success("Board updated");
        setEditing(null);
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const deactivate = useMutation(
    orpc.workforce.hiring.deactivateBoard.mutationOptions({
      onSuccess: async () => {
        toast.success("Board deactivated");
        setDeactivating(null);
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const rows = boards.data ?? [];
  const activeCount = rows.filter((board) => board.isActive).length;
  const facilityNames = new Map((facilities.data ?? []).map((f) => [f.id, f.name]));
  const facilityOptions = (facilities.data ?? []).map((facility) => ({
    id: facility.id,
    name: facility.name,
  }));

  const facilityNameFor = (board: BoardRow) =>
    board.facilityId
      ? (facilityNames.get(board.facilityId) ?? "Another facility")
      : "All facilities";
  const jobCountFor = (board: BoardRow) =>
    (jobs.data ?? []).filter((job) => job.boardId === board.id).length;

  const submitCreate = (values: BoardFormValues) => {
    const input = boardFormToInput(values);
    if (!input.facilityId) {
      toast.error("Choose a facility for this board");
      return;
    }
    create.mutate({
      facilityId: input.facilityId,
      name: input.name,
      description: input.description,
      slug: input.slug,
    });
  };

  const submitEdit = (values: BoardFormValues) => {
    if (!editing) return;
    const input = boardFormToInput(values);
    update.mutate({
      boardId: editing.id,
      name: input.name,
      description: input.description,
      slug: input.slug,
      /* The building is editable here as well as at creation: a board made against the wrong one
       * had no way back short of switching it off and starting again. Refused server-side where a
       * job from another building is already on it. */
      facilityId: input.facilityId || null,
      isActive: input.isActive,
    });
  };

  return (
    <>
      <PageHeader
        title="Job boards"
        count={boards.data ? rows.length : undefined}
        meta={
          boards.data
            ? `${activeCount} active, boards group postings across your facilities`
            : "Optional groupings for your postings."
        }
        actions={
          canManage ? <Button onClick={() => setCreating(true)}>New board</Button> : undefined
        }
      />

      {boards.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : boards.isError ? (
        <ReadFailed
          message="Could not load job boards."
          error={boards.error}
          onRetry={() => void boards.refetch()}
          retrying={boards.isFetching}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No job boards yet"
          description="Boards are optional. Create one to group related postings under a shared public page."
        />
      ) : (
        <JobBoardTable
          rows={rows}
          canManage={canManage}
          managesEverywhere={managesEverywhere}
          facilityNameFor={facilityNameFor}
          jobCountFor={jobCountFor}
          onEdit={(board) => setEditing(board)}
          onDeactivate={(board) => setDeactivating(board)}
        />
      )}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New job board</DialogTitle>
          </DialogHeader>
          <JobBoardForm
            mode="create"
            defaultValues={emptyBoard(facilityId === "all" ? "" : facilityId)}
            facilities={facilityOptions}
            pending={create.isPending}
            onCancel={() => setCreating(false)}
            onSubmit={submitCreate}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit job board</DialogTitle>
          </DialogHeader>
          {editing ? (
            <JobBoardForm
              mode="edit"
              defaultValues={boardToForm(editing)}
              facilities={facilityOptions}
              pending={update.isPending}
              onCancel={() => setEditing(null)}
              onSubmit={submitEdit}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deactivating !== null}
        onOpenChange={(open) => !open && setDeactivating(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate this job board?</AlertDialogTitle>
            <AlertDialogDescription>
              Postings already on this board keep it. It simply stops being offered for new
              postings, and you can switch it back on by editing it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => deactivating && deactivate.mutate({ boardId: deactivating.id })}
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
